




















































































































































/* General */
import Vue from 'vue'
import CardCanvas from '@/layouts/card-canvas.vue'

/* Mixins */

/* Components */
import DeletePersonModal from './components/delete-person-modal.vue'

/* Helpers */
import TypeAssistant from '@/helpers/type-assistant'
import RouteAssistant from '@/helpers/route-assistant'

/* Data types */
import { AccountRoles } from '@/types/defined-types'
import {
  IProjectWithMemberships,
  IPersonWithEmail,
  IAccountMembershipsOutputDetail,
  IItemForSelection,
} from '@/api/types/response.types'

/* API */
import AccountMembershipsAPI from '@/api/modules/account-memberships.api'

export default Vue.extend({
  name: 'PersonDetail',

  metaInfo(): any {
    return {
      title: this.person.name,
    }
  },

  components: {
    CardCanvas,
    DeletePersonModal,
  },

  data() {
    return {
      isLoading: true,
      isSubmitting: false,
      showMoreOptions: null,
      showDeletePersonModal: false,
      maxNumberAvatars: 3,
      avatarGallerySize: 24,
      AccountRoles,
      RouteAssistant,
      rowCount: 1,
      person: {} as IAccountMembershipsOutputDetail,
      mailboxes: [] as IProjectWithMemberships[],
      projects: [] as IProjectWithMemberships[],
      people: [] as IPersonWithEmail[],
      companies: [] as IItemForSelection[],
    }
  },

  computed: {
    accountID(): number {
      return parseInt(this.$route.params.accountId, 10)
    },
    noAccountProjects(): boolean {
      return this.projects.length === 0
    },
    noAccountMailboxes(): boolean {
      return this.mailboxes.length === 0
    },
    accountMembershipID(): number {
      return Number(this.$route.params.accountMembershipId)
    },
    accessGrantedEntityID(): number {
      if (!isNaN(Number(this.$route.query.access_granted))) {
        return Number(this.$route.query.access_granted)
      }
      return null
    },
    hasAllProjectsSelected(): boolean {
      return (
        this.person.listAssignedInternalProjectIDs.length ===
        this.projects.length
      )
    },
    hasAllMailboxesSelected(): boolean {
      return this.person.listAssignedMailboxIDs.length === this.mailboxes.length
    },
    availableRoles(): IItemForSelection[] {
      return this.$permissions.availableRolesToAdmin
    },
    sendInviteText(): string {
      return this.person.hasRegistered
        ? 'Email Password Reset'
        : 'Email Invitation'
    },
  },

  async created() {
    this.isLoading = true
    await Promise.all([
      this.getUpdateOptions(),
      this.getAccountMembershipDetail(),
    ])
    this.checkFromAccessGranted()
    this.isLoading = false
  },

  methods: {
    /* 
       If the incoming route has a querystring param of 'access_granted=[entityID]'
       and if the ID matches a mailbox or project the user is a part of, display
       success messaging. This is for handling redirects from the "Misc.GrantProjectAccess"
       route on the .NET backend for granting mailbox/internal project access requests
       so the granting user can see the update is made.
    */
    checkFromAccessGranted() {
      if (
        this.accessGrantedEntityID &&
        this.getEntityNameIfSelected(this.accessGrantedEntityID)
      ) {
        this.$messaging.showSuccess(
          this.person.name +
            ' is now a member of ' +
            this.getEntityNameIfSelected(this.accessGrantedEntityID),
        )
      }
    },
    addRow(): void {
      this.rowCount++
    },
    getEntityNameIfSelected(id: number): string {
      if (this.isProjectSelected(this.accessGrantedEntityID)) {
        return this.projects.find(p => p.id === id).title
      }
      if (this.isMailboxSelected(this.accessGrantedEntityID)) {
        return this.mailboxes.find(m => m.id === id).title
      }
      return null
    },
    isProjectSelected(id: number): boolean {
      return this.person.listAssignedInternalProjectIDs.includes(id)
    },
    isMailboxSelected(id: number): boolean {
      return this.person.listAssignedMailboxIDs.includes(id)
    },
    getPeopleInEntity(entity: IProjectWithMemberships): IPersonWithEmail[] {
      return this.people.filter(p => entity.listUserIDs.includes(p.id))
    },
    closeOptions() {
      this.showMoreOptions = true
      this.$nextTick(() => {
        this.showMoreOptions = false
      })
    },
    async getUpdateOptions() {
      try {
        const updateOptions = await AccountMembershipsAPI.get_UpdateOptions()
        this.mailboxes = updateOptions.listMailboxes
        this.projects = updateOptions.listInternalProjects
        this.companies = updateOptions.listCompanies
        this.people = updateOptions.listPeople
      } catch (error) {
        this.$error.emit(error)
      }
    },
    async getAccountMembershipDetail() {
      try {
        this.person = await AccountMembershipsAPI.get_Detail(
          this.accountMembershipID,
        )
      } catch (error) {
        this.$error.emit(error)
      }
    },
    async sendSignInEmail() {
      try {
        const response = await AccountMembershipsAPI.get_SendSignInEmail(
          this.accountMembershipID,
        )
        this.$messaging.showSuccess(response.message)
        this.closeOptions()
      } catch (error) {
        this.$error.emit(error, true)
      }
    },
    async addProject(id: number) {
      this.person.listAssignedInternalProjectIDs.push(id)
      this.updateEntityMemberships()
    },
    async removeProject(id: number) {
      this.person.listAssignedInternalProjectIDs = this.person.listAssignedInternalProjectIDs.filter(
        p => p !== id,
      )
      this.updateEntityMemberships()
    },
    async addMailbox(id: number) {
      this.person.listAssignedMailboxIDs.push(id)
      this.updateEntityMemberships()
    },
    async removeMailbox(id: number) {
      this.person.listAssignedMailboxIDs = this.person.listAssignedMailboxIDs.filter(
        m => m !== id,
      )
      this.updateEntityMemberships()
    },
    async selectAllProjects() {
      this.person.listAssignedInternalProjectIDs = this.projects.map(p => p.id)
      this.updateEntityMemberships()
    },
    async removeAllProjects() {
      this.person.listAssignedInternalProjectIDs = []
      this.updateEntityMemberships()
    },
    async selectAllMailboxes() {
      this.person.listAssignedMailboxIDs = this.mailboxes.map(p => p.id)
      this.updateEntityMemberships()
    },
    async removeAllMailboxes() {
      this.person.listAssignedMailboxIDs = []
      this.updateEntityMemberships()
    },
    async updateEntityMemberships() {
      this.isSubmitting = true

      const entityIDs = [
        ...this.person.listAssignedMailboxIDs,
        ...this.person.listAssignedInternalProjectIDs,
      ]

      try {
        const response = await AccountMembershipsAPI.put_UpdateProjectMemberships(
          this.accountMembershipID,
          entityIDs,
        )
        /* Regrab the update options to update mailbox/project lists to update counts */
        await this.getUpdateOptions()
        this.$messaging.showSuccess(response.message)
      } catch (error) {
        this.$error.emit(error, true)
        await this.getAccountMembershipDetail()
      }
      this.isSubmitting = false
    },

    async updateCompany() {
      try {
        const response = await AccountMembershipsAPI.put_UpdateCompany(
          this.accountMembershipID,
          this.person.companyID,
        )
        this.$messaging.showSuccess(response.message)
      } catch (error) {
        this.$error.emit(error, true)
      }
    },

    async updateAccountRole() {
      try {
        const response = await AccountMembershipsAPI.put_UpdateRole(
          this.accountMembershipID,
          this.person.accountRoleID,
        )
        this.$messaging.showSuccess(response.message)
      } catch (error) {
        this.$error.emit(error, true)
        await this.getAccountMembershipDetail()
      }
    },
  },
})
